// Word-game solvers on the public-domain ENABLE list (public/wordlists/enable1.txt.gz,
// the same file the unscrambler uses). The list is fetched once, on first use, so it
// never enters the JS bundle.

let wordsPromise: Promise<string[]> | null = null;

export function loadWords(): Promise<string[]> {
  if (!wordsPromise) {
    wordsPromise = (async () => {
      const response = await fetch('/wordlists/enable1.txt.gz');
      if (!response.ok || !response.body)
        throw new Error('The word list could not be loaded. Check your connection and retry.');
      const text = await new Response(
        response.body.pipeThrough(new DecompressionStream('gzip')),
      ).text();
      return text
        .split('\n')
        .map((w) => w.trim())
        .filter((w) => w.length >= 2);
    })().catch((error) => {
      wordsPromise = null;
      throw error;
    });
  }
  return wordsPromise;
}

// ---------- Wordle ----------

export type WordleClues = {
  /** Five slots; a letter where it is known (green), '' otherwise. */
  green: string[];
  /** Five slots; letters known to be in the word but NOT in that slot (yellow). */
  yellow: string[];
  /** Letters not in the word (grey). */
  grey: string;
};

export function wordleCandidates(words: string[], clues: WordleClues): string[] {
  const green = clues.green.map((c) => c.toLowerCase().trim().slice(0, 1));
  const yellow = clues.yellow.map((c) => c.toLowerCase().replace(/[^a-z]/g, ''));
  const mustHave = new Set([...green.join(''), ...yellow.join('')]);
  // A grey letter that is also green/yellow only means "no extra copies"; don't exclude it.
  const grey = new Set(
    [...clues.grey.toLowerCase().replace(/[^a-z]/g, '')].filter((c) => !mustHave.has(c)),
  );
  return words.filter((w) => {
    if (w.length !== 5) return false;
    for (let i = 0; i < 5; i += 1) {
      if (green[i] && w[i] !== green[i]) return false;
      if (yellow[i] && yellow[i].includes(w[i])) return false;
      if (grey.has(w[i])) return false;
    }
    for (const c of mustHave) if (!w.includes(c)) return false;
    return true;
  });
}

/** Rank by how common each letter is in its position among the remaining candidates. */
export function rankWordle(candidates: string[]): string[] {
  // Frequencies come from non-plural words: thousands of "-s" plurals would otherwise make
  // a final S look like the best possible letter.
  const isPlural = (w: string) => /[^s]s$/.test(w);
  const base = candidates.filter((w) => !isPlural(w));
  const freq = Array.from({ length: 5 }, () => new Map<string, number>());
  for (const w of base.length ? base : candidates)
    for (let i = 0; i < 5; i += 1) freq[i].set(w[i], (freq[i].get(w[i]) ?? 0) + 1);
  const score = (w: string) => {
    let s = 0;
    const seen = new Set<string>();
    for (let i = 0; i < 5; i += 1) {
      s += freq[i].get(w[i]) ?? 0;
      if (!seen.has(w[i])) s += 0.5 * candidates.length * 0.01;
      seen.add(w[i]);
    }
    // Repeated letters test fewer possibilities; prefer five distinct letters. Wordle
    // answers are almost never plurals ending in -s, so those drop down the list.
    return s * (seen.size === 5 ? 1 : 0.8) * (isPlural(w) ? 0.55 : 1);
  };
  return [...candidates].sort((a, b) => score(b) - score(a));
}

// ---------- Spelling Bee ----------

export type BeeWord = { word: string; points: number; pangram: boolean };

/** NYT scoring: 4 letters = 1 point, longer = 1 per letter, pangram +7. */
export function spellingBee(words: string[], letters: string, center: string): BeeWord[] {
  const set = new Set(letters.toLowerCase().replace(/[^a-z]/g, ''));
  const c = center.toLowerCase();
  if (!c || set.size < 2) return [];
  set.add(c);
  const out: BeeWord[] = [];
  for (const w of words) {
    if (w.length < 4 || !w.includes(c)) continue;
    let ok = true;
    for (const ch of w)
      if (!set.has(ch)) {
        ok = false;
        break;
      }
    if (!ok) continue;
    const pangram = [...set].every((ch) => w.includes(ch));
    out.push({ word: w, pangram, points: (w.length === 4 ? 1 : w.length) + (pangram ? 7 : 0) });
  }
  return out.sort(
    (a, b) =>
      Number(b.pangram) - Number(a.pangram) ||
      b.word.length - a.word.length ||
      a.word.localeCompare(b.word),
  );
}

// ---------- Crossword (pattern) ----------

/** Pattern with ?, _ or . for unknown letters: "c?t" matches cat, cot, cut. */
export function crosswordMatches(
  words: string[],
  pattern: string,
  include = '',
  exclude = '',
): string[] {
  const p = pattern.toLowerCase().replace(/\s+/g, '').replace(/[_.*]/g, '?');
  if (!p || /[^a-z?]/.test(p)) return [];
  const re = new RegExp(`^${p.replace(/\?/g, '[a-z]')}$`);
  const inc = [...include.toLowerCase().replace(/[^a-z]/g, '')];
  const exc = new Set(exclude.toLowerCase().replace(/[^a-z]/g, ''));
  return words.filter(
    (w) =>
      w.length === p.length &&
      re.test(w) &&
      inc.every((c) => w.includes(c)) &&
      ![...w].some((c, i) => p[i] === '?' && exc.has(c)),
  );
}

// ---------- Sudoku ----------

export type Grid = number[]; // 81 cells, 0 = empty

export function validGrid(g: Grid): { ok: boolean; conflict?: [number, number] } {
  for (let i = 0; i < 81; i += 1) {
    if (!g[i]) continue;
    for (let j = i + 1; j < 81; j += 1) {
      if (g[j] !== g[i]) continue;
      const sameRow = Math.floor(i / 9) === Math.floor(j / 9);
      const sameCol = i % 9 === j % 9;
      const sameBox =
        Math.floor(i / 27) === Math.floor(j / 27) &&
        Math.floor((i % 9) / 3) === Math.floor((j % 9) / 3);
      if (sameRow || sameCol || sameBox) return { ok: false, conflict: [i, j] };
    }
  }
  return { ok: true };
}

function candidates(g: Grid, i: number): number[] {
  const r = Math.floor(i / 9);
  const c = i % 9;
  const used = new Set<number>();
  for (let k = 0; k < 9; k += 1) {
    used.add(g[r * 9 + k]);
    used.add(g[k * 9 + c]);
  }
  const br = r - (r % 3);
  const bc = c - (c % 3);
  for (let dr = 0; dr < 3; dr += 1)
    for (let dc = 0; dc < 3; dc += 1) used.add(g[(br + dr) * 9 + bc + dc]);
  const out: number[] = [];
  for (let v = 1; v <= 9; v += 1) if (!used.has(v)) out.push(v);
  return out;
}

/** Backtracking with the most-constrained cell first. Returns null if unsolvable. */
export function solveSudoku(input: Grid, limit = 2): { solution: Grid | null; solutions: number } {
  const g = [...input];
  let count = 0;
  let first: Grid | null = null;
  const step = (): boolean => {
    let best = -1;
    let bestCands: number[] = [];
    for (let i = 0; i < 81; i += 1) {
      if (g[i]) continue;
      const cands = candidates(g, i);
      if (!cands.length) return false;
      if (best < 0 || cands.length < bestCands.length) {
        best = i;
        bestCands = cands;
        if (cands.length === 1) break;
      }
    }
    if (best < 0) {
      count += 1;
      if (!first) first = [...g];
      return count >= limit;
    }
    for (const v of bestCands) {
      g[best] = v;
      if (step()) return true;
    }
    g[best] = 0;
    return false;
  };
  step();
  return { solution: first, solutions: count };
}
